import React, { useState } from 'react';
import {
    Box, Typography, Stack, Divider, alpha, useTheme,
    Accordion, AccordionSummary, AccordionDetails,
    List, ListItem, ListItemIcon, ListItemText,
    Chip, Button, Tooltip, Collapse,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    Reddit as RedditIcon,
    Webhook as WebhookIcon,
    OpenInNew as OpenInNewIcon,
    ContentCopy as CopyIcon,
    CheckCircle as CheckIcon,
    Lightbulb as TipIcon,
    Shield as ShieldIcon,
    Bolt as BoltIcon,
    Info as InfoIcon,
} from '@mui/icons-material';

// ── Platform colours ───────────────────────────────────────────────────────────
const PLATFORM_META = {
    instagram: { color: '#E1306C', icon: <InstagramIcon /> },
    youtube:   { color: '#FF0000', icon: <YouTubeIcon /> },
    facebook:  { color: '#1877F2', icon: <FacebookIcon /> },
    twitter:   { color: '#1DA1F2', icon: <TwitterIcon sx={{ color: '#1DA1F2' }} /> },
    linkedin:  { color: '#0A66C2', icon: <LinkedInIcon /> },
    reddit:    { color: '#FF4500', icon: <RedditIcon /> },
    webhooks:  { color: '#6366f1', icon: <WebhookIcon /> },
};

// ── Full step-by-step platform guides ────────────────────────────────────────
// Each step is a string or { text, link, linkLabel }
const PLATFORM_GUIDES = [
    {
        id: 'instagram',
        title: 'Instagram Graph API Setup',
        tagline: 'OAuth 2.0 — long-lived token via Meta Developer App',
        sections: [
            {
                heading: 'Prerequisites — do these first',
                steps: [
                    'Your Instagram account must be a Business or Creator account (not personal).\n→ Instagram app → profile → ☰ → Settings and Privacy → Account type and tools → Switch to Professional Account → Business or Creator.',
                    'Your Instagram Business/Creator account must be linked to a Facebook Page you manage.\n→ Instagram → Settings → Account Center → Profiles → Add Instagram → link to your Facebook Page.',
                    'You must be the admin of both the Instagram account and the linked Facebook Page.',
                ],
            },
            {
                heading: 'Create a Meta Developer App',
                steps: [
                    { text: 'Go to Meta for Developers and log in with the Facebook account that manages your Page.', link: 'https://developers.facebook.com/', linkLabel: 'Open Meta for Developers' },
                    'Click "My Apps" (top-right) → "Create App".',
                    'When asked "What do you want your app to do?", select "Other" → Next.',
                    'For app type choose "Business" → Next.',
                    'Enter a display name (e.g. "Socialixer"), your contact email, and optionally link a Business Portfolio → "Create App".',
                ],
            },
            {
                heading: 'Add Facebook Login for Business + redirect URI',
                steps: [
                    'On the App Dashboard scroll to "Add products". Find "Facebook Login for Business" → "Set Up".',
                    'Choose "Web", enter your site URL, click Save. You can skip the Quickstart steps.',
                    'Left sidebar → Facebook Login for Business → Settings.',
                    'Paste your redirect URL (from the Developer Info box in Settings) into "Valid OAuth Redirect URIs". Click Save Changes.',
                ],
            },
            {
                heading: 'Enable required Instagram permissions',
                steps: [
                    'Left sidebar → "App Review" → "Permissions and Features".',
                    'Search for and confirm each permission:\n• instagram_basic\n• instagram_content_publish\n• instagram_manage_comments\n• instagram_manage_insights\n• instagram_manage_messages\n• pages_show_list\n• pages_read_engagement',
                    'In Development mode all permissions work for the app owner and test users without App Review.',
                    'For production: submit each permission for App Review. Business verification is required for publish permissions.',
                ],
            },
            {
                heading: 'Copy App ID and App Secret',
                steps: [
                    'Left sidebar → "App settings" → "Basic".',
                    'Copy the App ID (numeric) → paste into the App ID field in Settings.',
                    'Next to "App Secret" click "Show", enter your Facebook password, copy the secret → paste into App Secret in Settings.',
                    'Click "Secure Credentials".',
                ],
            },
            {
                heading: 'Connect via OAuth',
                steps: [
                    'Once App ID and App Secret are saved, an "OAuth Connection" section appears in Settings.',
                    'Click "Connect to Instagram" → you will be redirected to Facebook\'s OAuth dialog.',
                    'Log in with the Facebook account that manages your Page and approve all requested permissions.',
                    'You\'ll be redirected back automatically. You\'ll see "Instagram @yourusername connected successfully!" when complete.',
                    '⚡ Long-lived tokens last 60 days. The app will remind you to reconnect before expiry.',
                ],
            },
            {
                heading: 'Troubleshooting',
                steps: [
                    '"No Instagram Business or Creator account found" — your Instagram account is not linked to a Facebook Page, or it is still personal. Redo the Prerequisites step.',
                    '"Invalid Scopes" — a permission is missing in App Review → Permissions and Features. Go back and add the missing scope.',
                    '"Invalid redirect_uri" — the redirect URL in Valid OAuth Redirect URIs does not exactly match the one in the Developer Info box (check http vs https, trailing slashes).',
                ],
            },
        ],
    },
    {
        id: 'youtube',
        title: 'YouTube Automation (OAuth 2.0)',
        tagline: 'Google Cloud Console — YouTube Data API v3',
        sections: [
            {
                heading: 'Create a Google Cloud project & enable the API',
                steps: [
                    { text: 'Sign in to Google Cloud Console.', link: 'https://console.cloud.google.com', linkLabel: 'Open Google Cloud Console' },
                    { text: 'Click the project picker → "New Project" → give it any name → Create.', link: 'https://console.cloud.google.com/projectcreate', linkLabel: 'Create New Project' },
                    { text: 'Go to APIs & Services → Library, search "YouTube Data API v3", click Enable.', link: 'https://console.cloud.google.com/apis/library/youtube.googleapis.com', linkLabel: 'Enable YouTube API' },
                ],
            },
            {
                heading: 'Set up the OAuth consent screen',
                steps: [
                    { text: 'Open the OAuth consent screen. Set User Type to "External", fill in your app name and email, then save.', link: 'https://console.cloud.google.com/apis/credentials/consent', linkLabel: 'Open Consent Screen' },
                ],
            },
            {
                heading: 'Create OAuth credentials',
                steps: [
                    { text: 'Go to APIs & Services → Credentials → "+ Create Credentials" → "OAuth client ID".', link: 'https://console.cloud.google.com/apis/credentials', linkLabel: 'Open Credentials Page' },
                    'Choose Application type → "Web application".',
                    'Under "Authorised redirect URIs" add:\n• https://socialixer.com/settings\n• http://localhost:3000/settings',
                    'Click Create — copy the Client ID and Client Secret from the popup.',
                ],
            },
            {
                heading: 'Find your YouTube Channel ID',
                steps: [
                    { text: 'Go to YouTube Studio → Settings (gear icon, bottom-left) → Channel → Basic info tab.', link: 'https://studio.youtube.com', linkLabel: 'Open YouTube Studio' },
                    'Your Channel ID starts with "UC" followed by 22 characters. Copy it.',
                ],
            },
            {
                heading: 'Finish connecting in Settings',
                steps: [
                    'Paste your Client ID, Client Secret, and Channel ID into the Settings fields → click "Secure Credentials".',
                    'A "Connect to YouTube" button will appear — click it.',
                    'Choose the Google account that owns your YouTube channel and click Allow.',
                    'You\'ll be sent back automatically. When you see "Successfully connected to YouTube!" you\'re all set.',
                ],
            },
        ],
    },
    {
        id: 'facebook',
        title: 'Facebook Page Management',
        tagline: 'Meta OAuth 2.0 — permanent Page access token',
        sections: [
            {
                heading: 'Prerequisites',
                steps: [
                    'You must have a Facebook Page (not just a personal profile).',
                    'You must be an Admin of that Facebook Page.',
                    'Your Facebook account must be a registered Meta developer (free, instant).',
                ],
            },
            {
                heading: 'Create a Meta Developer App',
                steps: [
                    { text: 'Go to Meta for Developers and log in with the Facebook account that manages your Page.', link: 'https://developers.facebook.com/', linkLabel: 'Open Meta for Developers' },
                    'Click "My Apps" → "Create App" → "Other" → "Business" → Next.',
                    'Enter a display name and contact email → "Create App".',
                ],
            },
            {
                heading: 'Add Facebook Login for Business + redirect URI',
                steps: [
                    'On the App Dashboard → "Add products" → "Facebook Login for Business" → Set Up.',
                    'Choose "Web", enter your site URL, click Save.',
                    'Left sidebar → Facebook Login for Business → Settings.',
                    'Paste your redirect URL (from Developer Info box in Settings) into "Valid OAuth Redirect URIs". Click Save Changes.',
                ],
            },
            {
                heading: 'Enable required Facebook permissions',
                steps: [
                    'Left sidebar → "App Review" → "Permissions and Features".',
                    'Add each permission:\n• pages_show_list\n• pages_read_engagement\n• pages_manage_posts\n• pages_manage_engagement\n• pages_manage_metadata',
                    'In Development mode all permissions work for the app owner and Test Users instantly.',
                ],
            },
            {
                heading: 'Copy App ID and App Secret',
                steps: [
                    'Left sidebar → "App settings" → "Basic".',
                    'Copy the App ID → paste into App ID field in Settings.',
                    'Click "Show" next to App Secret → enter your password → copy → paste into Settings.',
                    'Click "Secure Credentials".',
                ],
            },
            {
                heading: 'Connect via OAuth',
                steps: [
                    'Once credentials are saved, click "Connect to Facebook" in Settings.',
                    'Log in and approve all requested permissions. Make sure you select the correct Page when prompted.',
                    'You\'ll be redirected back automatically. You\'ll see "Connected to Facebook Page \'Your Page Name\'" when complete.',
                    '⚡ The Page access token obtained this way does NOT expire unless you change your Facebook password or revoke the app.',
                ],
            },
        ],
    },
    {
        id: 'twitter',
        title: 'Twitter (X) — OAuth 1.0a',
        tagline: 'Twitter Developer Portal — all five credentials required',
        sections: [
            {
                heading: 'Apply for developer access',
                steps: [
                    { text: 'Go to the Twitter Developer Portal and sign up for a developer account.', link: 'https://developer.twitter.com/en/portal/dashboard', linkLabel: 'Open Developer Portal' },
                    'Answer the use-case questions — approval is usually instant for basic (free) access.',
                ],
            },
            {
                heading: 'Create a Project and App',
                steps: [
                    { text: 'Inside the Portal, click "+ New Project", give it a name, select a use case.', link: 'https://developer.twitter.com/en/portal/projects-and-apps', linkLabel: 'Open Projects & Apps' },
                    'Create a new App inside the project.',
                    'App Settings → "User authentication settings" → enable OAuth 1.0a.',
                    'Set App permissions to "Read and Write" — required for posting.',
                ],
            },
            {
                heading: 'Copy your credentials',
                steps: [
                    'In the "Keys and Tokens" tab, copy your API Key (Consumer Key) and API Key Secret.',
                    'Click "Generate" under "Access Token and Secret" — copy both values immediately (shown only once).',
                    'Copy the Bearer Token shown at the top of the same page.',
                    'All five values go into the Twitter fields in Settings.',
                ],
            },
        ],
    },
    {
        id: 'linkedin',
        title: 'LinkedIn Professional API',
        tagline: 'OAuth 2.0 access token — 60 day lifespan',
        sections: [
            {
                heading: 'Create a LinkedIn Developer App',
                steps: [
                    { text: 'Go to LinkedIn Developer Apps and click "Create app".', link: 'https://www.linkedin.com/developers/apps', linkLabel: 'Open LinkedIn Apps' },
                    'Fill in the app name, link it to a LinkedIn Company Page (required), upload a logo, then click Create App.',
                ],
            },
            {
                heading: 'Add required products',
                steps: [
                    'In your app, click the "Products" tab.',
                    'Request access to "Share on LinkedIn" and "Sign In with LinkedIn using OpenID Connect". Most apps get instant approval.',
                ],
            },
            {
                heading: 'Get an access token',
                steps: [
                    { text: 'Use the LinkedIn OAuth Token Generator to get a token without writing any code.', link: 'https://www.linkedin.com/developers/tools/oauth/token-generator', linkLabel: 'Open Token Generator' },
                    'Select your app, tick the scopes r_liteprofile and w_member_social, click "Request access token".',
                    'Sign in with your LinkedIn account when prompted.',
                    'Copy the access token — valid for 60 days. Paste it into the Access Token field in Settings.',
                ],
            },
        ],
    },
    {
        id: 'reddit',
        title: 'Reddit Script Automation',
        tagline: '"script" app type — uses your Reddit account credentials',
        sections: [
            {
                heading: 'Create a Reddit script app',
                steps: [
                    { text: 'Go to Reddit App Preferences (you must be logged into Reddit).', link: 'https://www.reddit.com/prefs/apps', linkLabel: 'Open Reddit Apps' },
                    'Scroll to the bottom and click "Create App" (or "Create another app…").',
                    'Fill in any name. Select type "script". Set redirect URI to http://localhost:8080 (required by Reddit, not actually used). Click Create App.',
                ],
            },
            {
                heading: 'Copy your credentials',
                steps: [
                    'After creating the app, the Client ID is the short string directly under the app name (below "personal use script").',
                    'The Client Secret is labeled "secret" in the same box.',
                    'Username and Password are your Reddit account login — Reddit uses these for script-type apps.',
                    'For User Agent, use this format: web:SocialixerApp:1.0 (by u/YourRedditUsername)',
                ],
            },
        ],
    },
    {
        id: 'webhooks',
        title: 'AI Engagement & Webhooks',
        tagline: 'Configure Meta Webhook callbacks for real-time lead detection',
        sections: [
            {
                heading: 'Instagram Webhook setup',
                steps: [
                    'In your Meta Developer App Dashboard → left sidebar → "Webhooks".',
                    'Click "Add Subscription" for the Instagram object.',
                    'Callback URL: your backend public URL + /webhook/instagram\n  e.g. https://api.socialixer.com/webhook/instagram',
                    'Subscribe to the fields: comments and messages.',
                    'Enter a Verify Token — any string you choose — and add the same value to your backend environment as INSTAGRAM_WEBHOOK_VERIFY_TOKEN.',
                ],
            },
            {
                heading: 'Facebook Webhook setup',
                steps: [
                    'In the same Webhooks section, add a subscription for the Page object.',
                    'Callback URL: your backend public URL + /webhook/facebook',
                    'Subscribe to: feed (for post comments) and messages.',
                ],
            },
            {
                heading: 'How AI engagement works',
                steps: [
                    'When a comment or DM arrives, Meta sends it to your webhook instantly.',
                    'The AI Safety Filter checks for spam, harassment, and hate speech first — blocked messages never reach the AI.',
                    'Lead Detection scans for pricing, availability, and buying-intent keywords automatically.',
                    'For identified leads, the AI drafts a personalised reply and (if auto-reply is enabled) sends it immediately.',
                    'All interactions appear in the Engagement Hub for manual review and override.',
                ],
            },
        ],
    },
];

// ── StepContent — renders a single step (string or rich object) ───────────────
function StepContent({ step, color }) {
    const isRich = typeof step === 'object';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7, whiteSpace: 'pre-line', color: 'text.primary' }}>
                {isRich ? step.text : step}
            </Typography>
            {isRich && step.link && (
                <Box>
                    <Button
                        component="a"
                        href={step.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                        sx={{
                            px: 1.5, py: 0.4, minHeight: 0,
                            fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', borderRadius: '6px',
                            color, bgcolor: alpha(color, 0.1), border: `1px solid ${alpha(color, 0.25)}`,
                            '&:hover': { bgcolor: alpha(color, 0.18) },
                        }}
                    >
                        {step.linkLabel || 'Open ↗'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

// ── CopyRow — small copy-to-clipboard row used in the webhook reference ───────
function CopyRow({ label, value }) {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 1.5, borderRadius: '10px',
            bgcolor: alpha(theme.palette.divider, isDark ? 0.08 : 0.05),
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
            <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>{label}</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{value}</Typography>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
                <Button
                    size="small" onClick={handleCopy}
                    startIcon={copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
                    sx={{
                        ml: 2, minWidth: 72, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.75rem',
                        color: copied ? '#10d98a' : '#6366f1',
                        bgcolor: alpha(copied ? '#10d98a' : '#6366f1', 0.08),
                        flexShrink: 0,
                    }}
                >
                    {copied ? 'Copied' : 'Copy'}
                </Button>
            </Tooltip>
        </Box>
    );
}

// ── Main Guide component ───────────────────────────────────────────────────────
const Guide = () => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const backendUrl  = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const frontendUrl = window.location.origin;

    const callbackUrls = [
        { label: 'YouTube Redirect URI',   value: `${frontendUrl}/settings` },
        { label: 'Facebook Redirect URI',  value: `${frontendUrl}/settings` },
        { label: 'Instagram Redirect URI', value: `${frontendUrl}/settings` },
        { label: 'Instagram Webhook URL',  value: `${backendUrl}/webhook/instagram` },
        { label: 'Facebook Webhook URL',   value: `${backendUrl}/webhook/facebook` },
    ];

    const [devUrlsOpen, setDevUrlsOpen] = useState(false);

    // shared card style — works in both light and dark (relies on global MuiCard override)
    const accordionSx = (color) => ({
        borderRadius: '20px !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.12 : 0.15)}`,
        bgcolor: isDark ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.85),
        backdropFilter: 'blur(16px)',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&.Mui-expanded': {
            borderColor: alpha(color, 0.35),
            boxShadow: `0 4px 24px ${alpha(color, isDark ? 0.12 : 0.07)}`,
        },
    });

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', py: 4, px: { xs: 2, md: 0 } }}>

            {/* ── Page header ───────────────────────────────────────────────── */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#10d98a', mb: 0.5 }}>
                    Setup
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>
                    Platform Setup Guide
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>
                    Follow these steps to connect each platform and enable AI lead detection &amp; auto-replies.
                </Typography>
            </Box>

            {/* ── Pro Tips ──────────────────────────────────────────────────── */}
            <Box sx={{
                mb: 4, p: 3.5, borderRadius: '24px',
                background: isDark
                    ? 'linear-gradient(135deg, rgba(16,217,138,0.12) 0%, rgba(99,102,241,0.12) 100%)'
                    : 'linear-gradient(135deg, rgba(16,217,138,0.08) 0%, rgba(99,102,241,0.06) 100%)',
                border: `1px solid ${alpha('#10d98a', 0.22)}`,
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: alpha('#10d98a', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TipIcon sx={{ color: '#10d98a', fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>Pro Tips</Typography>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: alpha(theme.palette.divider, 0.12) }} />}>
                    <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                        <BoltIcon sx={{ color: '#f59e0b', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.3 }}>Lead Detection</Typography>
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                                AI scans every comment and DM for pricing questions, availability, and buying intent — automatically.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                        <ShieldIcon sx={{ color: '#6366f1', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.3 }}>Safety First</Typography>
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                                Multi-stage filters block harassment and hate speech before the AI ever drafts a reply.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                        <WebhookIcon sx={{ color: '#a855f7', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.3 }}>Real-time Webhooks</Typography>
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                                Leads detected and replied to within seconds via webhook — no polling delay.
                            </Typography>
                        </Box>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Chip label="Real-time Ready" size="small" sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: alpha('#10d98a', 0.14), color: '#10d98a', border: `1px solid ${alpha('#10d98a', 0.3)}` }} />
                    <Chip label="AI-powered"      size="small" sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: alpha('#6366f1', 0.14), color: '#6366f1', border: `1px solid ${alpha('#6366f1', 0.3)}` }} />
                    <Chip label="Multi-platform"  size="small" sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: alpha('#f59e0b', 0.12), color: '#f59e0b', border: `1px solid ${alpha('#f59e0b', 0.3)}` }} />
                </Stack>
            </Box>

            {/* ── Developer callback URLs (collapsible) ─────────────────────── */}
            <Box sx={{
                mb: 4, borderRadius: '20px',
                bgcolor: alpha('#6366f1', isDark ? 0.07 : 0.04),
                border: `1px dashed ${alpha('#6366f1', devUrlsOpen ? 0.4 : 0.25)}`,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
            }}>
                {/* Header row — always visible, click to toggle */}
                <Stack
                    direction="row" alignItems="center" justifyContent="space-between"
                    onClick={() => setDevUrlsOpen(o => !o)}
                    sx={{ px: 3, py: 2, cursor: 'pointer', userSelect: 'none' }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <InfoIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 800, color: '#6366f1', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Developer &amp; Callback URLs
                        </Typography>
                    </Stack>
                    <ExpandMoreIcon sx={{
                        color: '#6366f1', fontSize: 20,
                        transform: devUrlsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.25s ease',
                    }} />
                </Stack>

                {/* Collapsible body */}
                <Collapse in={devUrlsOpen}>
                    <Box sx={{ px: 3, pb: 3 }}>
                        <Typography color="text.secondary" sx={{ fontSize: '0.875rem', mb: 2 }}>
                            Use these exact URLs when configuring redirect URIs and webhooks in the Google, Meta, and other developer portals.
                        </Typography>
                        <Stack spacing={1.5}>
                            {callbackUrls.map(item => (
                                <CopyRow key={item.label} label={item.label} value={item.value} />
                            ))}
                        </Stack>
                    </Box>
                </Collapse>
            </Box>

            {/* ── Platform accordions ────────────────────────────────────────── */}
            <Stack spacing={2} sx={{ mb: 4 }}>
                {PLATFORM_GUIDES.map((platform, idx) => {
                    const meta  = PLATFORM_META[platform.id];
                    const color = meta.color;

                    return (
                        <Accordion key={platform.id} defaultExpanded={idx === 0} disableGutters elevation={0} sx={accordionSx(color)}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color }} />}
                                sx={{ px: 3, py: 1, minHeight: 64, '& .MuiAccordionSummary-content': { my: 0 } }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                                        bgcolor: alpha(color, isDark ? 0.15 : 0.1),
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {React.cloneElement(meta.icon, { sx: { color, fontSize: 20 } })}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                                            {platform.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                            {platform.tagline}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </AccordionSummary>

                            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                                <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.1) }} />
                                <Stack spacing={3.5}>
                                    {platform.sections.map((section, si) => (
                                        <Box key={si}>
                                            {/* Section heading */}
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                                <Box sx={{
                                                    width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
                                                    bgcolor: alpha(color, isDark ? 0.2 : 0.12),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color }}>
                                                        {si + 1}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{
                                                    fontWeight: 800, fontSize: '0.72rem',
                                                    textTransform: 'uppercase', letterSpacing: '0.09em',
                                                    color,
                                                }}>
                                                    {section.heading}
                                                </Typography>
                                            </Stack>

                                            {/* Steps */}
                                            <List dense disablePadding>
                                                {section.steps.map((step, stepIdx) => (
                                                    <ListItem key={stepIdx} disablePadding sx={{ alignItems: 'flex-start', mb: 1.5, pl: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 28, mt: 0.4 }}>
                                                            <Box sx={{
                                                                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                                                bgcolor: alpha(color, isDark ? 0.18 : 0.12),
                                                                border: `1.5px solid ${alpha(color, 0.4)}`,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color }}>
                                                                    {stepIdx + 1}
                                                                </Typography>
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            disableTypography
                                                            primary={<StepContent step={step} color={color} />}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>

                                            {si < platform.sections.length - 1 && (
                                                <Divider sx={{ mt: 2, borderColor: alpha(theme.palette.divider, 0.08) }} />
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>

        </Box>
    );
};

export default Guide;
